/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { ColorPalette, UIColorPalette } from './ColorPalette';

export enum GameButtonTheme {
  TutorialNext = 'tutorial-next',
  SelfPlayerView = 'self-player-view',
}

export type GameButtonProps = {
  theme: GameButtonTheme;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const GameButton = forwardRef<HTMLButtonElement, GameButtonProps>(
  function GameButton({ theme, ...rest }, ref) {
    return (
      <button
        ref={ref}
        css={[GameButtonStyles.base, GameButtonThemeStyles[theme]]}
        {...rest}
      />
    );
  },
);

export default GameButton;

const GameButtonStyles = {
  base: css({
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'none',

    borderRadius: 5,
    boxShadow: `1px 1px 3px ${UIColorPalette.tutorialButtonShadow}`,
  }),
};

type ButtonThemeColorScheme = {
  background: string;
  borderColor?: string;
  textColor: string;
  hoverBackground: string;
  activeBackground: string;
};
export function makeGameButtonTheme(scheme: ButtonThemeColorScheme) {
  return css({
    backgroundColor: scheme.background,
    border: scheme.borderColor && `1px solid ${scheme.borderColor}`,
    color: scheme.textColor,
    '&:hover:enabled': {
      backgroundColor: scheme.hoverBackground,
    },
    '&:active:enabled': {
      backgroundColor: scheme.activeBackground,
    },
  });
}

const GameButtonThemeStyles: Record<GameButtonTheme, ReturnType<typeof css>> = {
  [GameButtonTheme.TutorialNext]: css(
    makeGameButtonTheme({
      background: ColorPalette.darkPurpleBG,
      borderColor: UIColorPalette.tutorialButtonBorder,
      textColor: ColorPalette.brightText,
      hoverBackground: ColorPalette.lightBG,
      activeBackground: ColorPalette.darkerPurpleBG,
    }),
    css({
      '&:disabled': {
        color: ColorPalette.dimText,
        borderColor: UIColorPalette.tutorialButtonBorderDisabled,
        boxShadow: `0px 0px 0px rgb(0, 0, 0)`,
      },
    }),
  ),
  [GameButtonTheme.SelfPlayerView]: css(
    makeGameButtonTheme({
      background: 'rgb(234, 100, 47)',
      borderColor: ColorPalette.printBlack,
      textColor: ColorPalette.printBlack,
      hoverBackground: 'rgb(250, 110, 55)',
      activeBackground: 'rgb(201, 86, 40)',
    }),
    css({
      '&:disabled': {
        background: 'rgb(138, 60, 29)',
      },
    }),
  ),
};
